import { Fragment } from 'react';
import { CheckCircle2, Lightbulb, Lock, ThumbsUp, Wrench, X } from 'lucide-react';
import { Card } from './ui/card';

// The four stages a recommendation travels through — mirrors the lifecycle
// badge colours (Open → Accepted → Completed → Validated).
const FLOW = [
  {
    icon: Lightbulb,
    label: 'Lizrd proposes',
    sub: 'A proven saving, with the evidence',
    color: 'var(--muted)',
  },
  { icon: ThumbsUp, label: 'You accept', sub: 'Commit to making the change', color: 'var(--iris)' },
  {
    icon: Wrench,
    label: 'You execute',
    sub: 'With our step-by-step guidance',
    color: 'var(--viridian)',
  },
  {
    icon: CheckCircle2,
    label: 'Lizrd validates',
    sub: 'We confirm the saving & close it',
    color: 'var(--iris-strong)',
  },
] as const;

/** A simple left-to-right flow of the recommendation lifecycle. */
function WorkflowDiagram() {
  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
      <div className="mb-2.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
        How a recommendation moves
      </div>
      <ol className="flex flex-col gap-2 sm:flex-row sm:items-start">
        {FLOW.map((s, i) => (
          <Fragment key={s.label}>
            <li className="flex flex-1 items-center gap-2.5 sm:flex-col sm:gap-2 sm:text-center">
              <s.icon aria-hidden className="h-6 w-6 flex-none" style={{ color: s.color }} />
              <div>
                <div className="text-xs font-semibold text-bone">{s.label}</div>
                <div className="text-[0.65rem] leading-snug text-muted">{s.sub}</div>
              </div>
            </li>
            {i < FLOW.length - 1 && (
              <span
                aria-hidden
                className="ml-[1.15rem] text-muted sm:ml-0 sm:mt-2.5 sm:self-start sm:pt-1"
              >
                <span className="hidden sm:inline">→</span>
                <span className="sm:hidden">↓</span>
              </span>
            )}
          </Fragment>
        ))}
      </ol>
    </div>
  );
}

// How to read the Optimizations page — what we surface, how it's ranked,
// and what you're expected to do with it.
const POINTS = [
  {
    title: 'What we surface',
    body: 'Concrete waste we can prove from your domain — orphaned resources, idle compute, and storage or network you pay for but barely use. Each finding comes with the evidence behind it.',
  },
  {
    title: 'Ranked by impact',
    body: 'Every item carries an estimated monthly saving and a confidence based on how closely our estimate tracks your real cloud bill — the biggest, most certain wins rise to the top.',
  },
  {
    title: 'Yours to action',
    body: 'A plain next step for each, plus where the resource is defined in code when we can trace it. Advice only — Lizrd never changes your cloud for you.',
  },
] as const;

/**
 * The Cost Optimization page's "how to read this page" guide — mirrors the
 * Explorer and Settings guides: what we surface, how it's ranked, and that it's
 * read-only advice. Controlled by the page (opened from the "How it works" link
 * in the header); renders nothing when closed.
 */
export function CostOptimizationGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;

  return (
    <Card className="relative space-y-4 p-5">
      <button
        onClick={onClose}
        aria-label="Hide guide"
        className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-sm border border-border text-muted transition-colors hover:bg-surface-2 hover:text-bone"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>

      <div className="space-y-1 pr-8">
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-iris">
          Optimizations
        </div>
        <h2 className="font-display text-lg font-bold text-bone">
          Optimizing your Infrastructure — The Workflow Explained
        </h2>
        <p className="text-sm text-haze">
          These are deterministic optimizations — no guesswork, just what your domain shows.
        </p>
      </div>

      <ol className="grid gap-5 sm:grid-cols-3">
        {POINTS.map((point, i) => (
          <li key={point.title} className="flex h-full flex-col">
            <div className="font-mono text-2xl font-bold leading-none text-iris tnum">
              {String(i + 1).padStart(2, '0')}
            </div>
            <h3 className="mt-2.5 font-semibold text-bone">{point.title}</h3>
            <p className="mt-1.5 text-xs leading-snug text-muted">{point.body}</p>
          </li>
        ))}
      </ol>

      <WorkflowDiagram />

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-iris/25 bg-iris/5 px-3 py-2 text-sm">
        <Lock aria-hidden className="h-4 w-4 flex-none text-iris" />
        <span className="text-haze">
          Read-only — we surface the opportunity;{' '}
          <span className="text-bone">you decide what to change.</span>
        </span>
      </div>
    </Card>
  );
}
